/* =========================================================================
   ClueVine — persistence
   A typed, versioned wrapper over localStorage. Each slice (settings, profile,
   stats, session) declares a version and a migrator. Reads are defensive:
   missing, malformed, or wrong-version data falls back to defaults rather than
   throwing, and writes silently no-op when storage is unavailable (private
   mode / quota). When localStorage can't be used at all, an in-memory backend
   keeps the app fully functional for the session.
   ========================================================================= */

const NAMESPACE = 'cluevine';

interface Backend {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  keys(): string[];
}

function memoryBackend(): Backend {
  const map = new Map<string, string>();
  return {
    getItem: (k) => (map.has(k) ? map.get(k)! : null),
    setItem: (k, v) => void map.set(k, v),
    removeItem: (k) => void map.delete(k),
    keys: () => [...map.keys()],
  };
}

function resolveBackend(): Backend {
  try {
    const probe = `${NAMESPACE}:__probe__`;
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    const ls = window.localStorage;
    return {
      getItem: (k) => ls.getItem(k),
      setItem: (k, v) => ls.setItem(k, v),
      removeItem: (k) => ls.removeItem(k),
      keys: () => {
        const out: string[] = [];
        for (let i = 0; i < ls.length; i++) {
          const k = ls.key(i);
          if (k) out.push(k);
        }
        return out;
      },
    };
  } catch {
    return memoryBackend();
  }
}

const backend: Backend = resolveBackend();

interface Envelope<T> {
  v: number;
  data: T;
}

export interface SliceConfig<T> {
  /** Unqualified slice name, e.g. "settings". */
  name: string;
  version: number;
  defaults: () => T;
  /** Upgrade older payloads. Return null to discard and use defaults. */
  migrate?: (fromVersion: number, data: unknown) => T | null;
}

function keyFor(name: string): string {
  return `${NAMESPACE}:${name}`;
}

export function loadSlice<T>(cfg: SliceConfig<T>): T {
  try {
    const raw = backend.getItem(keyFor(cfg.name));
    if (!raw) return cfg.defaults();

    const parsed = JSON.parse(raw) as Partial<Envelope<unknown>>;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.v !== 'number') {
      return cfg.defaults();
    }

    if (parsed.v === cfg.version) {
      return (parsed.data as T) ?? cfg.defaults();
    }

    if (parsed.v < cfg.version && cfg.migrate) {
      const migrated = cfg.migrate(parsed.v, parsed.data);
      if (migrated != null) {
        saveSlice(cfg, migrated);
        return migrated;
      }
    }
    // A newer (e.g. after a rolled-back deploy) or unmigratable payload: park a
    // copy before the provider's first save overwrites it with defaults.
    backupRaw(cfg.name, parsed.v, raw);
    return cfg.defaults();
  } catch {
    // Corrupt JSON or unexpected shape — recover gracefully.
    return cfg.defaults();
  }
}

function backupRaw(name: string, version: number, raw: string): void {
  try {
    const key = `${keyFor(name)}.bak.v${version}`;
    if (backend.getItem(key) == null) backend.setItem(key, raw);
  } catch {
    /* best effort */
  }
}

export function saveSlice<T>(cfg: SliceConfig<T>, data: T): void {
  try {
    const envelope: Envelope<T> = { v: cfg.version, data };
    backend.setItem(keyFor(cfg.name), JSON.stringify(envelope));
  } catch {
    /* quota exceeded or storage disabled — keep playing in memory */
  }
}

export function clearSlice(name: string): void {
  try {
    backend.removeItem(keyFor(name));
  } catch {
    /* ignore */
  }
}

/**
 * Remove every ClueVine key — used by "reset all data" in Settings. Slices named
 * in `keep` (e.g. the paid supporter unlock, owner config) survive.
 */
export function clearAll(keep: readonly string[] = []): void {
  const kept = new Set(keep.map(keyFor));
  try {
    for (const k of backend.keys()) {
      if (k.startsWith(`${NAMESPACE}:`) && !kept.has(k)) backend.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

/** Subscribe to another tab/window changing a slice. Returns an unsubscribe. */
export function onSliceChange(name: string, cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  const key = keyFor(name);
  const handler = (e: StorageEvent) => {
    if (e.key === key) cb();
  };
  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}

/** Exposed for tests so the in-memory backend can be exercised deterministically. */
export const __testing = { memoryBackend, keyFor };
